<template>
  <div class="task-kanban-filter-groupping-dropdown">
    <TirPmOptionList
      :width="280"
      @checkOpenOptions="emits('checkOpenOptionsFilterGroupping', $event)"
    >
      <template #optionListTarget="{ isOpened }">
        <div class="task-kanban-filter-groupping-dropdown__target">
          <QueueListIcon class="task-kanban-filter-groupping-dropdown__target-icon" />
          <span class="task-kanban-filter-groupping-dropdown__target-text">{{ targetText }}</span>
          <ChevronDownIcon
            :class="[
              'task-kanban-filter-groupping-dropdown__target-chevron',
              { 'is-opened': isOpened },
            ]"
          />
        </div>
      </template>
      <template #content>
        <TirPmScrollbar class="scrollbar" :show-on-hover="true">
          <div class="task-kanban-filter-groupping-dropdown__option-list">
            <div
              class="task-kanban-filter-groupping-dropdown__option"
              v-for="option in options"
              :key="option.GrouppingType"
              @click="selectOption(option.GrouppingType)"
            >
              <component
                :is="option.IconComponent"
                class="task-kanban-filter-groupping-dropdown__option-icon"
              />
              <span class="task-kanban-filter-groupping-dropdown__option-text">
                {{ option.Text }}
              </span>
            </div>
          </div>
        </TirPmScrollbar>
      </template>
    </TirPmOptionList>
  </div>
</template>

<script lang="ts" setup>
import {
  QueueListIcon,
  ChevronDownIcon,
  StopCircleIcon,
  UserIcon,
  TagIcon,
  RectangleGroupIcon,
  UserCircleIcon,
  CrossLinedIcon,
  RectangleStackIcon,
} from "tir-style-system/icons/outline";
import { FlagIcon, StatusIcon } from "tir-style-system/icons/solid";
import { PropType, computed, onBeforeMount, ref } from "vue";
import { TasksKanbanGrouppingTypeEnum } from "../enums";
import { TirPmOptionList } from "tir-components";
import { TirPmScrollbar } from "tir-components";

const emits = defineEmits<{
  (e: "update:modelValue", value: TasksKanbanGrouppingTypeEnum): void;

  (e: "checkOpenOptionsFilterGroupping", value: boolean);
}>();

const props = defineProps({
  modelValue: {
    type: Number as PropType<TasksKanbanGrouppingTypeEnum>,
    required: true,
  },
  /**Разрешено отсутствие группировки */
  allowNoneGroupping: { type: Boolean },
});

const targetText = computed(() => {
  let grouppingName: string = "";

  switch (props.modelValue) {
    case TasksKanbanGrouppingTypeEnum.Status:
      grouppingName = "Статус";
      break;
    case TasksKanbanGrouppingTypeEnum.Performer:
      grouppingName = "Исполнитель";
      break;
    case TasksKanbanGrouppingTypeEnum.Priority:
      grouppingName = "Приоритет";
      break;
    case TasksKanbanGrouppingTypeEnum.Type:
      grouppingName = "Тип";
      break;
    case TasksKanbanGrouppingTypeEnum.Tag:
      grouppingName = "Тег";
      break;
    case TasksKanbanGrouppingTypeEnum.Author:
      grouppingName = "Автор";
      break;
    case TasksKanbanGrouppingTypeEnum.None:
      return "Без группировки";
  }

  return `Группировка по: ${grouppingName}`;
});

const options = ref([
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Status,
    IconComponent: StatusIcon,
    Text: "Статус",
  },
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Performer,
    IconComponent: UserCircleIcon,
    Text: "Исполнитель",
  },
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Priority,
    IconComponent: FlagIcon,
    Text: "Приоритет",
  },
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Tag,
    IconComponent: TagIcon,
    Text: "Тег",
  },
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Type,
    IconComponent: RectangleStackIcon,
    Text: "Тип",
  },
  {
    GrouppingType: TasksKanbanGrouppingTypeEnum.Author,
    IconComponent: UserIcon,
    Text: "Автор",
  },
]);

/**Перед рендером */
onBeforeMount(() => {
  if (props.allowNoneGroupping) {
    options.value.unshift({
      GrouppingType: TasksKanbanGrouppingTypeEnum.None,
      IconComponent: CrossLinedIcon,
      Text: "Без",
    });
  }
});

/**
 * * Выбрать опцию
 * @param grouppingType
 */
const selectOption = (grouppingType: TasksKanbanGrouppingTypeEnum) => {
  emits("update:modelValue", grouppingType);
  // Клик вне компонента для закрытия
  document.body.click();
};
</script>

<style lang="scss" scoped>
.scrollbar {
  max-height: 18rem;
}
.task-kanban-filter-groupping-dropdown {
  &__target {
    display: flex;
    align-items: center;
    column-gap: 0.375rem;
    color: var(--text-primary);
    cursor: pointer;
    user-select: none;
    padding: 0.375rem 0.75rem;
  }

  &__target-text {
    font-size: 0.875rem;
    font-weight: 500;
  }

  &__target-icon {
    width: 1rem;
    height: 1rem;
  }

  &__target-chevron {
    width: 1rem;
    height: 1rem;
    transition: transform 0.2s;

    &.is-opened {
      transform: rotate(180deg);
    }
  }

  &__option-list {
    display: flex;
    flex-direction: column;
  }

  &__option {
    display: flex;
    align-items: center;
    column-gap: 0.75rem;
    color: var(--neutral-100);
    padding: 0.75rem;
    cursor: pointer;

    &:hover {
      background-color: var(--neutral-translucent-5);
    }

    &:active {
      background-color: var(--neutral-translucent-10);
    }
  }

  &__option-icon {
    width: 1.5rem;
    height: 1.5rem;
  }

  &__option-text {
    font-size: 1rem;
    line-height: 1.5rem;
  }
}
</style>
